import React, {
  useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { FileArchive, Send, ListChecks } from 'lucide-react';

import useLeaveReconciliationsApi from './config';
import useBulkJobs from './config/useBulkJobs';
import { BULK_KIND } from './config/bulk';
import BulkStartDrawer from './components/BulkStartDrawer';
import BulkJobsDrawer from './components/BulkJobsDrawer';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DOCUMENT_ACCEPT from '../../utils/documentAccept';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import CheckboxInput from '../../components/ui/CheckboxInput';
import { getDepartments } from '../../services/departmentService';

// UI ALTIN KURALLARI: başlık YALNIZ DataTable'da — üstte ayrı bir başlık şeridi
// yok. Yıl/durum seçicileri de DataTable'ın KENDİ araç çubuğunda (toolbarActions,
// bkz. DataTable.js yorumu + LicensesPage/HrAnalyticsRecruitingPage emsali) —
// LeaveBalanceReportPage'in tablo ÜSTÜNDE ayrı bir `<Bar>` şeridi kullandığı
// eski desen BİLİNÇLİ OLARAK burada YOK (Görev 12 brief'i: "üstte ayrı başlık
// şeridi yok; başlık yalnız DataTable'da").
const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* İç içe scroll yasak — tek kaydırma DataTable satırlarıdır. */
  min-height: 0;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SelectBox = styled.div`
  width: 140px;
`;

// Departman seçici ad taşıdığı için yıl/durumdan geniş — LeaveBalanceReportPage
// FieldBox (220px) ile AYNI ölçü.
const WideSelectBox = styled.div`
  width: 220px;
`;

// Kapsam hatası (İK değil + astı yok) — LeaveBalanceReportPage.ScopeHint ile AYNI desen.
const ScopeHint = styled.div`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
`;

// Toplu seçim şeridi — AtsApplicationsPage/MyGoalsPage deseni (sayfa-yerel checkbox
// kolonu + yalnız ≥1 satır seçiliyken çizilen şerit). DataTable'ın kendi seçimi
// KULLANILMADI: o yalnız `onRowsDelete`e kablolu ve sayfa değişince sıfırlanır;
// burada seçim sayfalar ARASINDA korunur (İK 3 sayfadan kişi toplayabilsin).
const BulkBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: rgba(2, 145, 239, 0.06);
  border: 1px solid rgba(2, 145, 239, 0.18);
  border-radius: 6px;
`;

const BulkCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const BulkNote = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const BulkSpacer = styled.span`
  flex: 1;
`;

const SelectCell = styled.div``;

// "Toplu İşler" düğmesindeki etkin-iş noktası.
const BadgeWrap = styled.span`
  position: relative;
  display: inline-flex;
`;

const ActiveDot = styled.span`
  position: absolute;
  top: 1px;
  right: 1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: ${({ theme }) => theme.colors.primary.main};
  pointer-events: none;
`;

const currentYear = new Date().getFullYear();
// Son 6 yıl (bugünkü yıl dahil) — PayrollSummaryPage.yearOptions ile AYNI aralık.
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => {
  const y = currentYear - 5 + i;
  return { id: y, name: String(y) };
});

export default function LeaveReconciliationsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();
  const location = useLocation();
  // Toplu işler formları dondurur ve e-posta gönderir — tekil "Yeniden Üret/Teslim
  // Al" ile aynı sınıf, `LM.Write` ister (backend de ister).
  const canBulk = canWrite('LM');

  const [year, setYear] = useState(currentYear);
  const [status, setStatus] = useState('all');
  const [departmentId, setDepartmentId] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [deliverTarget, setDeliverTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  // Bulgu G — dökümü bilerek tazeleme. Onay ZORUNLU: eylem donmuş dökümü
  // (çalışanın eline basılı olarak gitmiş olabilecek kümeyi) değiştirir.
  const [regenerateTarget, setRegenerateTarget] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  // Toplu seçim: çalışan kimlikleri (backend "selected" kapsamı EmployeeIds alır).
  // Sayfa ve durum/departman süzgeci değişince KORUNUR; yıl değişince sıfırlanır —
  // başka bir yılın listesinden yapılmış seçimle iş başlatmak şaşırtıcı olurdu.
  const [selectedIds, setSelectedIds] = useState([]);
  // `kind` kapanırken de korunur — kapanış animasyonunda başlık zıplamasın.
  const [bulkDrawer, setBulkDrawer] = useState({ open: false, kind: BULK_KIND.ZIP });
  const [jobsOpen, setJobsOpen] = useState(false);
  const startedRef = useRef(false);

  const effectiveParams = useMemo(() => ({
    ...queryParams,
    year,
    status: status === 'all' ? undefined : status,
    departmentId: departmentId || undefined,
  }), [queryParams, year, status, departmentId]);

  const {
    fetching, columns: baseColumns, data, pagination, scopeError,
    refresh, downloadPdf, regenerate, deliver, revoke, statusLabels,
  } = useLeaveReconciliationsApi(effectiveParams);

  // Bir iş bittiğinde satırların durumu (hazırlandı / bildirim tarihi) değişmiştir.
  const bulk = useBulkJobs({ enabled: canBulk, onJobsFinished: refresh });
  const { load: loadJobs } = bulk;

  // Bitiş e-postasındaki "Toplu İşleri Görüntüle" bağlantısı (?bulkJobs=1) paneli
  // açar; parametre sonra adresten silinir (yenilemede panel yeniden açılmasın).
  useEffect(() => {
    if (!canBulk) return;
    const params = new URLSearchParams(location.search);
    if (params.get('bulkJobs') !== '1') return;
    setJobsOpen(true);
    params.delete('bulkJobs');
    const search = params.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [canBulk, location.search, location.pathname, history]);

  const statusOptions = useMemo(() => ([
    { id: 'all', name: t('leaveReconciliation.statusAll') },
    { id: 'draft', name: statusLabels.draft },
    { id: 'generated', name: statusLabels.generated },
    { id: 'delivered', name: statusLabels.delivered },
  ]), [t, statusLabels]);

  // Yıl/durum değişince sayfalama sıfırlanır (EmployeesPage.handleStatusChange ile
  // AYNI desen) — aksi halde 3. sayfadayken yıl değiştirince boş bir sayfa görünür.
  const handleYearChange = useCallback((val) => {
    setYear(val);
    setSelectedIds([]);
    setQueryParams((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  const handleStatusChange = useCallback((val) => {
    setStatus(val || 'all');
    setQueryParams((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  // §12.6 departman süzgeci (final inceleme, M-2): backend
  // (LeaveReconciliationFilter.DepartmentId) baştan beri uygulanmıştı ama
  // hiçbir istemciden ERİŞİLEMİYORDU. Seçici LeaveBalanceReportPage ile AYNI
  // desendedir: AsyncGenericSelect + `onSearch` (asyncConfig ÖLÜ alandır,
  // yalnız onSearch okunur — bkz. reference_genericformdrawer_asyncconfig_tuzagi).
  const handleDepartmentChange = useCallback((val) => {
    setDepartmentId(val || null);
    setQueryParams((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  const searchDepartments = useCallback(async (query) => {
    const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
    const res = await getDepartments(params);
    const result = res.data?.data;
    return Array.isArray(result) ? result : (result?.data || []);
  }, []);

  // Yalnız sayfalama — kolonların hiçbiri kendi filtresini/sıralamasını TAŞIMAZ
  // (bkz. config/index.js columns yorumu); List ucu zaten orderBy/filtre almıyor.
  const handleDataStateChange = useCallback((action, tableState) => {
    const parsed = parseTableParams(tableState, baseColumns);
    setQueryParams({ pageNumber: parsed.pageNumber, pageSize: parsed.pageSize });
  }, [baseColumns]);

  const handleDownload = useCallback((row) => {
    downloadPdf(row.userId, row.year);
  }, [downloadPdf]);

  const handleRegenerateConfirm = useCallback(async () => {
    if (!regenerateTarget) return;
    setRegenerating(true);
    await regenerate(regenerateTarget.userId, regenerateTarget.year);
    setRegenerating(false);
    setRegenerateTarget(null);
  }, [regenerateTarget, regenerate]);

  // Teslim: yalnız `generated` durumundan mümkün (backend I-4a) — `draft`
  // satırda görünmez, önce "PDF İndir" kaydı üretir.
  // Geri alma: yalnız `delivered` durumundan mümkün (backend M-6).
  // Yeniden üretim (bulgu G): yalnız `generated` — `draft`ta ortada donmuş bir
  // döküm YOK (indirme onu zaten üretir), `delivered`da ise ıslak imzalı beyanın
  // dayanağı değiştirilemez (backend de reddeder); İK önce geri almalıdır.
  // İNDİRMEDEN AYRI bir eylemdir: indirme donmuş dökümü basar, bu onu YENİLER.
  const buildActions = useCallback((row) => {
    const actions = [
      { type: 'download', label: t('leaveReconciliation.downloadPdf'), onClick: () => handleDownload(row) },
    ];
    if (canWrite('LM')) {
      if (row.status === 'generated') {
        actions.push({ type: 'regenerate', label: t('leaveReconciliation.regenerate'), onClick: () => setRegenerateTarget(row) });
        actions.push({ type: 'upload', label: t('leaveReconciliation.deliver'), onClick: () => setDeliverTarget(row) });
      }
      if (row.status === 'delivered') {
        actions.push({ type: 'return_to_draft', label: t('leaveReconciliation.revoke'), onClick: () => setRevokeTarget(row) });
      }
    }
    return actions;
  }, [t, canWrite, handleDownload]);

  const toggleSelect = useCallback((employeeId) => {
    setSelectedIds((ids) => (ids.includes(employeeId)
      ? ids.filter((x) => x !== employeeId)
      : [...ids, employeeId]));
  }, []);

  const pageIds = useMemo(() => data.map((r) => r.employeeId), [data]);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  // Başlık kutusu YALNIZ görünen sayfayı kapsar; "listedeki herkes" kapsamı toplu
  // işlem çekmecesindedir (sunucu tarafı süzgeçle — sayfalar tek tek gezilmez).
  const toggleSelectPage = useCallback(() => {
    setSelectedIds((ids) => (allOnPageSelected
      ? ids.filter((id) => !pageIds.includes(id))
      : [...ids, ...pageIds.filter((id) => !ids.includes(id))]));
  }, [allOnPageSelected, pageIds]);

  const openBulk = useCallback((kind) => setBulkDrawer({ open: true, kind }), []);

  const openJobs = useCallback(() => {
    setJobsOpen(true);
    loadJobs();
  }, [loadJobs]);

  const { start: startJob } = bulk;
  const handleBulkStart = useCallback(async (request) => {
    const ok = await startJob(request);
    if (ok) startedRef.current = true;
    return ok;
  }, [startJob]);

  // Seçim, çekmece KAPANIRKEN temizlenir — açıkken temizlense çekmecenin kapsamı
  // "listedeki herkes"e döner ve kapanmadan önce boşa (tüm şirket için) bir
  // önizleme sorgusu gider.
  const handleBulkClose = useCallback(() => {
    setBulkDrawer((prev) => ({ ...prev, open: false }));
    if (startedRef.current) {
      startedRef.current = false;
      setSelectedIds([]);
      setJobsOpen(true);
    }
  }, []);

  const columns = useMemo(() => ([
    ...(canBulk ? [{
      name: 'bulkSelect',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false, viewColumns: false,
        customHeadLabelRender: () => (
          <SelectCell data-role="bulk-select-page">
            <CheckboxInput
              aria-label={t('leaveReconciliation.bulk.selectPage')}
              checked={allOnPageSelected}
              disabled={pageIds.length === 0}
              onChange={toggleSelectPage}
            />
          </SelectCell>
        ),
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return (
            <SelectCell data-role={`bulk-select-${row.employeeId}`}>
              <CheckboxInput
                aria-label={t('leaveReconciliation.bulk.selectRow', { name: row.fullName })}
                checked={selectedIds.includes(row.employeeId)}
                onChange={() => toggleSelect(row.employeeId)}
              />
            </SelectCell>
          );
        },
      },
    }] : []),
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row ? <OptionsMenu actions={buildActions(row)} row={row} /> : null;
        },
      },
    },
  ]), [
    t, canBulk, baseColumns, data, buildActions,
    selectedIds, toggleSelect, allOnPageSelected, pageIds, toggleSelectPage,
  ]);

  const deliverSchema = useMemo(() => ([
    {
      name: 'signedDocumentUrl',
      label: t('leaveReconciliation.signedDocumentUrl'),
      type: 'pictureUpload',
      required: true,
      // purpose ZORUNLU — backend StoragePurposes.Private kümesine bu amaç için
      // özel eklendi (Görev 12 brief'i); verilmezse sunucu Private varsayar ama
      // niyeti açıkça yazmak driftten korur (bkz. employeeDocument uploadSchema).
      purpose: 'leaveReconciliationDocument',
      accept: DOCUMENT_ACCEPT,
      maxSize: 10,
    },
    { name: 'note', label: t('leaveReconciliation.note'), type: 'textarea' },
  ]), [t]);

  const revokeSchema = useMemo(() => ([
    {
      name: 'note',
      label: t('leaveReconciliation.revokeNote'),
      type: 'textarea',
      required: true,
      description: t('leaveReconciliation.noteRequired'),
    },
  ]), [t]);

  const handleDeliverSave = useCallback(async (formData) => {
    if (!deliverTarget) return false;
    const success = await deliver(deliverTarget.userId, deliverTarget.year, formData);
    if (success) setDeliverTarget(null);
    return success;
  }, [deliverTarget, deliver]);

  const handleRevokeSave = useCallback(async (formData) => {
    if (!revokeTarget) return false;
    const success = await revoke(revokeTarget.userId, revokeTarget.year, formData);
    if (success) setRevokeTarget(null);
    return success;
  }, [revokeTarget, revoke]);

  return (
    <Container>
      <GenericFormDrawer
        open={!!deliverTarget}
        onClose={() => setDeliverTarget(null)}
        title={t('leaveReconciliation.deliverTitle', { name: deliverTarget?.fullName || '', year: deliverTarget?.year || '' })}
        schema={deliverSchema}
        initialValues={deliverTarget ? {} : null}
        onSave={handleDeliverSave}
      />

      <GenericFormDrawer
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title={t('leaveReconciliation.revokeTitle', { name: revokeTarget?.fullName || '', year: revokeTarget?.year || '' })}
        schema={revokeSchema}
        initialValues={revokeTarget ? {} : null}
        onSave={handleRevokeSave}
      />

      {canBulk && (
        <>
          <BulkStartDrawer
            open={bulkDrawer.open}
            kind={bulkDrawer.kind}
            onClose={handleBulkClose}
            onStart={handleBulkStart}
            year={year}
            selectedIds={selectedIds}
            status={status}
            departmentId={departmentId}
          />
          <BulkJobsDrawer
            open={jobsOpen}
            onClose={() => setJobsOpen(false)}
            jobs={bulk.jobs}
            loading={bulk.loading}
            failed={bulk.failed}
            onRetry={loadJobs}
            onDownload={bulk.download}
            onCancel={bulk.cancel}
          />
        </>
      )}

      <ConfirmDialog
        isOpen={!!regenerateTarget}
        title={t('leaveReconciliation.regenerateTitle')}
        message={t('leaveReconciliation.regenerateMessage', {
          name: regenerateTarget?.fullName || '',
          year: regenerateTarget?.year || '',
        })}
        confirmText={t('leaveReconciliation.regenerate')}
        onConfirm={handleRegenerateConfirm}
        onCancel={() => setRegenerateTarget(null)}
        loading={regenerating}
      />

      {canBulk && !scopeError && selectedIds.length > 0 && (
        <BulkBar data-role="bulk-bar">
          <BulkCount data-role="bulk-selected-count">
            {t('leaveReconciliation.bulk.selectedCount', { count: selectedIds.length })}
          </BulkCount>
          <BulkNote>{t('leaveReconciliation.bulk.selectionNote')}</BulkNote>
          <BulkSpacer />
          <Button variant="outline" data-role="bulk-clear" onClick={() => setSelectedIds([])}>
            {t('leaveReconciliation.bulk.clearSelection')}
          </Button>
          <Button
            variant="outline"
            data-role="bulk-bar-zip"
            disabled={bulk.hasActive}
            onClick={() => openBulk(BULK_KIND.ZIP)}
          >
            <FileArchive size={14} />
            {' '}
            {t('leaveReconciliation.bulk.zipAction')}
          </Button>
          <Button
            data-role="bulk-bar-notify"
            disabled={bulk.hasActive}
            onClick={() => openBulk(BULK_KIND.NOTIFY)}
          >
            <Send size={14} />
            {' '}
            {t('leaveReconciliation.bulk.notifyAction')}
          </Button>
        </BulkBar>
      )}

      {scopeError ? (
        <ScopeHint>{t(scopeError)}</ScopeHint>
      ) : (
        <DataTable
          title={t('leaveReconciliation.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          disableFilters
          onTableChanged={handleDataStateChange}
          toolbarActions={(
            <Toolbar>
              <SelectBox>
                <GenericSelect
                  label={t('leaveReconciliation.year')}
                  size="small"
                  clearable={false}
                  options={YEAR_OPTIONS}
                  value={year}
                  onChange={handleYearChange}
                />
              </SelectBox>
              <SelectBox>
                <GenericSelect
                  label={t('leaveReconciliation.statusLabel')}
                  size="small"
                  clearable={false}
                  options={statusOptions}
                  value={status}
                  onChange={handleStatusChange}
                />
              </SelectBox>
              <WideSelectBox>
                <AsyncGenericSelect
                  compact
                  label={t('leaveReconciliation.department')}
                  value={departmentId}
                  onChange={handleDepartmentChange}
                  onSearch={searchDepartments}
                />
              </WideSelectBox>
              {canBulk && (
                <>
                  {/* Süren kendi işi varken yeni iş başlatılamaz (sunucu kiracı başına
                      tek etkin iş tutar) — düğme kilitlenir, ipucu nedenini söyler. */}
                  <Tooltip text={bulk.hasActive ? t('leaveReconciliation.bulk.jobRunningHint') : t('leaveReconciliation.bulk.zipAction')}>
                    <IconButton
                      size="sm"
                      data-role="bulk-zip"
                      aria-label={t('leaveReconciliation.bulk.zipAction')}
                      disabled={bulk.hasActive}
                      onClick={() => openBulk(BULK_KIND.ZIP)}
                    >
                      <FileArchive size={16} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip text={bulk.hasActive ? t('leaveReconciliation.bulk.jobRunningHint') : t('leaveReconciliation.bulk.notifyAction')}>
                    <IconButton
                      size="sm"
                      data-role="bulk-notify"
                      aria-label={t('leaveReconciliation.bulk.notifyAction')}
                      disabled={bulk.hasActive}
                      onClick={() => openBulk(BULK_KIND.NOTIFY)}
                    >
                      <Send size={16} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip text={t('leaveReconciliation.bulk.jobsAction')}>
                    <BadgeWrap>
                      <IconButton
                        size="sm"
                        data-role="bulk-jobs-open"
                        aria-label={t('leaveReconciliation.bulk.jobsAction')}
                        onClick={openJobs}
                      >
                        <ListChecks size={16} />
                      </IconButton>
                      {bulk.hasActive && <ActiveDot data-role="bulk-active-dot" />}
                    </BadgeWrap>
                  </Tooltip>
                </>
              )}
            </Toolbar>
          )}
        />
      )}
    </Container>
  );
}
